import { gql } from '@apollo/client';
import { Box, Table, Tbody, Td, Text, Th, Thead, Tr, VStack } from '@chakra-ui/react';
import { TableInstance } from 'react-table';
import { ChevronDownIcon, ChevronUpIcon } from '~components/ui/icons';
import { defaultPageSizes } from '~components/ui/PageSizeSelector';
import { isWaveSubscription } from '~utils/subscriptions';
import { SubscriptionCustomersTableCustomerFragment as CustomerFragment } from './__generated__/SubscriptionCustomersTable.graphql';
import { SubscriptionCustomersTableFilter } from './Filtering';
import {
  SubscriptionCustomersTablePageSizeSelector,
  SubscriptionCustomersTablePagination,
} from './Pagination';

interface SubscriptionCustomersTableProps {
  table: TableInstance<CustomerFragment>;
  currentCustomerIds: string[];
}

export function SubscriptionCustomersTable({
  table,
  currentCustomerIds,
}: SubscriptionCustomersTableProps) {
  const { getTableProps, getTableBodyProps, headerGroups, prepareRow, page } = table;

  return (
    <VStack spacing="6" alignItems="stretch">
      <Text color="gray.500">Select customers to link them to this subscription plan.</Text>{' '}
      <SubscriptionCustomersTableFilter table={table} />
      <Table {...getTableProps()} variant="simple" width="100%" marginTop="8">
        <Thead>
          {headerGroups.map((headerGroup) => {
            const headerGroupProps = headerGroup.getHeaderGroupProps();

            return (
              <Tr {...headerGroupProps} key={headerGroupProps.key}>
                {headerGroup.headers.map((column) => {
                  const sortProps = column.getSortByToggleProps();
                  const sortColumn = sortProps.onClick;
                  const headerProps = column.getHeaderProps(sortProps);

                  return (
                    <Th
                      {...headerProps}
                      key={headerProps.key}
                      color={column.isSorted ? 'gray.700' : 'gray.600'}
                      minWidth={`${column.minWidth}px`}
                      maxWidth={`${column.maxWidth}px`}
                      width={column.width}
                      _hover={
                        column.disableSortBy
                          ? {}
                          : {
                              textColor: 'gray.800',
                            }
                      }
                      onClick={sortColumn}
                    >
                      <Box display="flex" flexDirection="row" alignItems="center">
                        <Box flex="1">{column.render('Header')}</Box>
                        {column.canSort && (
                          <Box>
                            {column.isSorted ? (
                              column.isSortedDesc ? (
                                <ChevronDownIcon display="inherit" />
                              ) : (
                                <ChevronUpIcon display="inherit" />
                              )
                            ) : (
                              <ChevronDownIcon visibility="hidden" />
                            )}
                          </Box>
                        )}
                      </Box>
                    </Th>
                  );
                })}
              </Tr>
            );
          })}
        </Thead>
        <Tbody {...getTableBodyProps()}>
          {page.map((row) => {
            prepareRow(row);
            const rowProps = row.getRowProps();
            const isDisabled =
              !currentCustomerIds.includes(row.original.id) &&
              isWaveSubscription(row.original.waveSubscription);

            return (
              <Tr
                {...rowProps}
                key={rowProps.key}
                {...(isDisabled ? { background: 'gray.50' } : { background: 'blue.' })}
              >
                {row.cells.map((cell) => {
                  const cellProps = cell.getCellProps();
                  return (
                    <Td
                      {...cellProps}
                      {...(isDisabled ? { opacity: 0.5 } : {})}
                      key={cellProps.key}
                      minWidth={`${cell.column.minWidth}px`}
                      maxWidth={`${cell.column.maxWidth}px`}
                      width={cell.column.width}
                    >
                      {cell.render('Cell')}
                    </Td>
                  );
                })}
              </Tr>
            );
          })}
        </Tbody>
      </Table>
      <Box display="flex">
        <Box flex="1">
          <SubscriptionCustomersTablePageSizeSelector
            table={table}
            sizes={[15, ...defaultPageSizes]}
          />
        </Box>
        <SubscriptionCustomersTablePagination table={table} />
      </Box>
    </VStack>
  );
}

SubscriptionCustomersTable.graphql = {
  fragments: {
    SubscriptionCustomersTableCustomer: gql`
      fragment SubscriptionCustomersTableCustomer on Customer {
        id
        name
        displayCount
        waveSubscription {
          id
        }
      }
    `,
  },
};
